import * as React from 'react'
import type { BreadcrumbOverride } from '@/components/Layout'

interface BreadcrumbContextValue {
  overrides: BreadcrumbOverride
  setBreadcrumbOverrides: (overrides: BreadcrumbOverride | ((prev: BreadcrumbOverride) => BreadcrumbOverride)) => void
}

const BreadcrumbContext = React.createContext<BreadcrumbContextValue | null>(null)

export function BreadcrumbProvider({ children }: { children: React.ReactNode }) {
  const [overrides, setOverrides] = React.useState<BreadcrumbOverride>({})

  const setBreadcrumbOverrides = React.useCallback(
    (value: BreadcrumbOverride | ((prev: BreadcrumbOverride) => BreadcrumbOverride)) => {
      setOverrides(
        typeof value === 'function'
          ? value
          : (prev) => ({ ...prev, ...value })
      )
    },
    []
  )

  const value = React.useMemo<BreadcrumbContextValue>(
    () => ({ overrides, setBreadcrumbOverrides }),
    [overrides, setBreadcrumbOverrides]
  )

  return (
    <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>
  )
}

export function useBreadcrumb(): BreadcrumbContextValue {
  const context = React.useContext(BreadcrumbContext)
  if (!context) {
    return {
      overrides: {},
      setBreadcrumbOverrides: () => {},
    }
  }
  return context
}

export function useSetBreadcrumbLabel() {
  const { setBreadcrumbOverrides } = useBreadcrumb()
  return React.useCallback(
    (key: keyof BreadcrumbOverride, value: string | undefined) => {
      setBreadcrumbOverrides((prev) => ({ ...prev, [key]: value }))
    },
    [setBreadcrumbOverrides]
  )
}
